import Button from "components/common/Button";
import CustomToast from "components/common/CustomToast";
import Dropdown from "components/common/Dropdown";
import TextInput from "components/common/InputField/TextInput";
import Loader from "components/common/Loader";
import VendorAvatar from "components/common/VendorAvatar";
import { displayFileIcon } from "helpers";
import { isFileSizeLessThanOrEqualTo } from "helpers/compareFileSizeInMb";
import { convertBytes } from "helpers/displayFileSize";
import { getFileTypeFromUrl } from "helpers/getFileTypeFromUrl";
import { useGetSingleSpace } from "hooks/spaceHooks";
import useGetSpacesFiles from "hooks/spaceHooks/useGetSpacesFiles";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { debounce, truncate } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import Skeleton from "react-loading-skeleton";
import { useParams } from "react-router-dom";
import { spaceActions } from "redux/Ldb/actions/spaceActions";
import { Space } from "redux/Ldb/types/spaceTypes";
import { firebaseService } from "services/firebaseService";

type SpaceDetailsFileTabProps = {
    spaceData: Space | null;
};

export default function SpaceDetailsFileTab(props: SpaceDetailsFileTabProps) {
    const { spaceData } = props;
    const dispatch = useAppDispatch();
    const { spaceId } = useParams();
    const [uploadProgress, setUploadProgress] = useState<{ progress: number }>({ progress: 0 });
    const [sharedBy, setSharedBy] = useState<string>("");
    const [refetch, setRefetch] = useState<boolean>(false);
    const [uploadingDoc, setUploadingDoc] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<string>("");
    const [filesAttached, setFilesAttached] = useState<{
        url: string;
        path: string;
        fileType: string;
        filename: string;
        fileSize?: string;
    }>({
        url: "",
        path: "",
        fileType: "",
        filename: "",
        fileSize: "",
    });
    const scrollContainer = document.getElementById("scrollContainer");

    const uploadTaskRef = useRef<any>(null);
    const { createSpaceFileSuccess, deleteSpaceFileSuccess, deletingSpaceFile } = useAppSelector(
        (state) => state.space
    );

    const {
        spaceFiles,
        gettingSpaceFiles,
        handleDebouncedSearch,
        memberId,
        setMemberId,
        fileTypes,
        setFileTypes,
        pagination,
        setPagination,
        search,
        setSearch,
        isLoadingMore,
        fetching,
    } = useGetSpacesFiles(scrollContainer, spaceId, refetch);

    const handleCreateSpaceFile = (url, path, fileType, fileName, fileSize) => {
        const body = {
            url: url,
            path: path,
            fileType: fileType,
            filename: fileName,
            fileSize: fileSize,
        };
        dispatch(spaceActions.createSpaceFile(spaceId, body));
    };

    useEffect(() => {
        if (Boolean(createSpaceFileSuccess)) {
            setFilesAttached({
                url: "",
                path: "",
                fileType: "",
                filename: "",
            });
            setRefetch(!refetch);
            dispatch(spaceActions.resetCreateSpaceFile());
        }
    }, [dispatch, createSpaceFileSuccess]);

    useEffect(() => {
        if (Boolean(deleteSpaceFileSuccess)) {
            toast.custom((t) => <CustomToast t={t} message={"File deleted"} type={"success"} />);
            setRefetch(!refetch);
            dispatch(spaceActions.resetDeleteSpaceFile());
            setDeleteId("");
        }
    }, [dispatch, deleteSpaceFileSuccess]);

    const deleteAttachment = (id: string) => {
        dispatch(spaceActions.deleteSpaceFile(spaceId, id));
    };

    const handleUpload = (fileName, file, fileSize) => {
        setUploadingDoc(true);
        const extension = fileName?.split(".").pop()?.toLowerCase() || "";
        const task = firebaseService.uploadFile(
            `synthally/${process.env.REACT_APP_STAGE}/space-files/${fileName}`,
            file,
            (url) => {
                url !== "error" && handleCreateSpaceFile(url, url, extension, fileName, fileSize);
                setUploadingDoc(false);
            },
            100,
            (prog) => {
                setUploadProgress(prog);
            }
        );
        uploadTaskRef.current = task;
    };

    const handleCancel = () => {
        if (uploadTaskRef.current) {
            try {
                uploadTaskRef.current.cancel();
                setFilesAttached({
                    url: "",
                    path: "",
                    fileType: "",
                    filename: "",
                });
                setUploadingDoc(false);
                setUploadProgress({ progress: 0 });
            } catch (error) {
                console.error("Error canceling upload:", error);
            }
        } else {
            console.log("No upload task to cancel.");
        }
    };
    const fileTypeOptions = [
        {
            label: "Images (JPG, PNG, GIF, SVG)",
            value: "jpg,jpeg,png,gif,svg",
        },
        {
            label: "PDFs",
            value: "pdf",
        },
        {
            label: "Presentations",
            value: "ppt,pptx,odp",
        },
        {
            label: "Spreadsheets",
            value: "xls,xlsx,csv,ods",
        },
        {
            label: "Documents",
            value: "doc,docx,txt,odt,rtf",
        },
        {
            label: "Audio",
            value: "mp3,wav,aac,flac,ogg",
        },
        {
            label: "Compressed Files",
            value: "zip,rar,7z,tar,gz,tgz",
        },
    ];

    return (
        <div className="px-[24px]">
            <div className="sticky top-0 bg-white z-10 flex items-center space-x-2 mt-4 mb-6 max-sm:grid max-sm:space-x-0 max-sm:space-y-2 max-sm:grid-cols-1">
                <TextInput
                    name={"search"}
                    value={search}
                    type={"text"}
                    inputPlaceholder={"Search files"}
                    inputClassName={"!h-[38px] pl-[0px] !bg-white text-sm !mb-0"}
                    onChange={(e) => {
                        setSearch(e.target.value);
                    }}
                    onInput={handleDebouncedSearch}
                    containerClassname={"!w-full"}
                    leftIcon={
                        <i className="ri-search-line before:content-['\f0d1] text-slate-400 mt-[2px]"></i>
                    }
                    inputContainerClassname={
                        "!rounded-[4px] max-sm:!w-full !pl-[25px] !h-[40px] !bg-white !border !border-slate-200"
                    }
                />

                <Dropdown
                    name="filter"
                    value={""}
                    dropdownTriggerClassName={" "}
                    handleChange={(name, value) => {
                        setFileTypes(value);
                    }}
                    dropdown={
                        <div className="bg-white flex items-center max-sm:justify-between h-10 gap-2 py-2.5 px-3 shadow-boxShadow-2 border border-slate-200 !rounded">
                            <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium">
                                {fileTypes
                                    ? fileTypeOptions?.find((item) => item?.value === fileTypes)
                                          ?.label
                                    : "File type"}
                            </span>

                            <div className="flex items-center space-x-2">
                                {fileTypes && (
                                    <i
                                        onClick={(e) => {
                                            e?.stopPropagation();
                                            setFileTypes("");
                                        }}
                                        className="ri-close-circle-fill text-sm text-slate-500"
                                    ></i>
                                )}
                                <i className="ri-arrow-down-s-line text-lg text-gm-50"></i>
                            </div>
                        </div>
                    }
                    wholeContainerClass={`max-sm:!w-full`}
                    dropdownItemsClasses=" !py-2"
                    dropdownClassName={`shadow-cardShadow-4 border border-slate-100 rounded !max-h-fit`}
                    dropdownContainerClasses={`shadow-[0px_4px_6px_-2px_rgba(16,24,40,0.03),0px_12px_16px_-4px_rgba(16,24,40,0.08)] -right-0  overflow-y-auto   border border-slate-200 !w-[159px] rounded !max-h-fit !py-0`}
                    dropdownOptions={fileTypeOptions}
                />

                <Dropdown
                    name="filter"
                    value={""}
                    dropdownTriggerClassName={" "}
                    handleChange={(name, value) => {
                        setMemberId(value?.split("//")[0]);
                        setSharedBy(value?.split("//")[1]);
                    }}
                    dropdown={
                        <div className="bg-white flex items-center max-sm:justify-between h-10 gap-2 py-2.5 px-3 shadow-boxShadow-2 border border-slate-200 !rounded">
                            <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium">
                                {sharedBy ? truncate(sharedBy, { length: 15 }) : "Shared by"}
                            </span>

                            <div className="flex items-center space-x-2">
                                {sharedBy && (
                                    <i
                                        onClick={(e) => {
                                            e?.stopPropagation();
                                            setSharedBy("");
                                            setMemberId("");
                                        }}
                                        className="ri-close-circle-fill text-sm text-slate-500"
                                    ></i>
                                )}
                                <i className="ri-arrow-down-s-line text-lg text-gm-50"></i>
                            </div>
                        </div>
                    }
                    wholeContainerClass={`max-sm:!w-full`}
                    dropdownItemsClasses=" !py-2"
                    dropdownClassName={`shadow-cardShadow-4 border border-slate-100 rounded !max-h-fit`}
                    dropdownContainerClasses={`shadow-[0px_4px_6px_-2px_rgba(16,24,40,0.03),0px_12px_16px_-4px_rgba(16,24,40,0.08)] -right-0 !h-[250px] overflow-y-auto   border border-slate-200  rounded !max-h-fit !py-0  `}
                    dropdownOptions={spaceData?.members?.map((member) => {
                        const name =
                            member?.type === "guest"
                                ? `${
                                      member?.firstName || member?.lastName
                                          ? `${member?.firstName || ""} ${member?.lastName || ""}`
                                          : member?.guest?.email
                                  }`
                                : member?.ldb?.fullName || member?.ldb?.email;
                        return {
                            label: (
                                <div className="flex justify-between max-w-[350px] gap-2">
                                    <div className="flex items-center gap-2">
                                        <VendorAvatar
                                            size={16}
                                            imageSrc={
                                                member?.type === "guest"
                                                    ? ""
                                                    : member?.ldb?.profile?.avatar || ""
                                            }
                                            name={name}
                                        />
                                        {name}
                                    </div>

                                    {member?.type === "guest" && (
                                        <p className="text-sm text-slate-500 ">Guest</p>
                                    )}
                                </div>
                            ),
                            value: `${
                                member?.type === "guest" ? member?.guestId : member?.ldbId
                            }//${name}`,
                        };
                    })}
                />

                <div
                    className="flex items-center gap-4 cursor-pointer"
                    onClick={() => document.getElementById("fileInput")?.click()}
                >
                    <div className=" !py-2.5 !px-4 w-[110px] bg-y-10 rounded text-sm font-rocGroteskMedium !text-gm-50">
                        Upload file
                    </div>
                    <input
                        type="file"
                        id="fileInput"
                        className={`absolute z-[-1]  opacity-0`}
                        onChange={(evt) => {
                            if (evt?.target?.files?.length > 0) {
                                if (!isFileSizeLessThanOrEqualTo(evt.target.files[0], 50)) {
                                    toast.custom((t) => (
                                        <CustomToast
                                            t={t}
                                            message={
                                                "The file size is too large. Please upload a file smaller than 50 MB."
                                            }
                                            type={"error"}
                                        />
                                    ));
                                    return;
                                }
                                setFilesAttached({
                                    ...filesAttached,
                                    fileSize: evt.target.files[0].size.toString(),
                                    filename: evt.target.files[0].name,
                                    fileType: evt.target.files[0].type,
                                    url: "",
                                    path: "",
                                });
                                handleUpload(
                                    evt.target.files[0].name,
                                    evt.target.files[0],
                                    evt.target.files[0].size.toString()
                                );
                            }
                        }}
                        disabled={uploadingDoc}
                    />
                </div>
            </div>

            <div className="max-h-[600px] h-[70vh] overflow-y-auto" id="scrollContainer">
                {uploadingDoc && (
                    <div className="flex space-x-1 w-full items-center">
                        <div className="w-full mb-2  py-3 px-4 rounded-[6px] border border-slate-100">
                            <div className="flex items-center ">
                                <div className="flex w-full space-x-3 items-center">
                                    {displayFileIcon(
                                        getFileTypeFromUrl(filesAttached?.filename),
                                        true,
                                        "w-[25px] h-[25px]"
                                    )}
                                    <div>
                                        <p className="text-sm font-rocGroteskMedium text-gm-50">
                                            {filesAttached?.filename}
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-rocGroteskMedium text-slate-500">
                                                {convertBytes(
                                                    Number(
                                                        (Math.round(uploadProgress?.progress) /
                                                            100) *
                                                            Number(filesAttached?.fileSize ?? 0)
                                                    )
                                                )}{" "}
                                                of{" "}
                                                {convertBytes(Number(filesAttached?.fileSize ?? 0))}
                                            </p>
                                            <p className="h-1 w-1 bg-slate-500 rounded-full"></p>
                                            <p className="text-sm font-rocGroteskMedium text-[#0AAD0A]">
                                                Uploading... {Math.round(uploadProgress?.progress)}%
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <Button
                                    btnClassname="!w-fit !py-2.5 !px-4  !bg-n-20 text-sm font-rocGroteskMedium !text-gm-50"
                                    btnText="Cancel"
                                    onClick={() => {
                                        handleCancel();
                                    }}
                                />
                            </div>
                            <div className="w-full h-1 bg-[#F2F4F7] relative left-0 mt-2">
                                <p
                                    className="absolute left-0 top-0  h-full bg-[#0AAD0A]"
                                    style={{ width: `${Math.round(uploadProgress?.progress)}%` }}
                                ></p>
                            </div>
                        </div>
                    </div>
                )}

                {fetching && !isLoadingMore && !search && !memberId && !fileTypes ? (
                    <div className="">
                        {[1, 2, 3, 4, 5]?.map((item, index) => {
                            return (
                                <div key={index} className="">
                                    <Skeleton
                                        height={50}
                                        width="100%"
                                        highlightColor="#E2E8F0"
                                        borderRadius={16}
                                        baseColor="#F1F5F9"
                                        className="custom-skeleton"
                                    />
                                </div>
                            );
                        })}
                    </div>
                ) : spaceFiles?.length > 0 ? (
                    <>
                        {" "}
                        {spaceFiles?.map((doc, idx) => (
                            <div key={idx} className="flex space-x-1 w-full items-center ">
                                <div className="w-full mb-2  py-3 px-4 rounded-[6px] border border-slate-100">
                                    <div className="flex items-center ">
                                        <div className="flex w-full space-x-3 items-center">
                                            {displayFileIcon(
                                                doc?.filename?.split(".").pop()?.toLowerCase() ||
                                                    "",
                                                true,
                                                "w-[25px] h-[25px]"
                                            )}
                                            <div>
                                                <p
                                                    className="text-sm font-rocGroteskMedium text-gm-50 cursor-pointer"
                                                    onClick={() => {
                                                        const a = document.createElement("a");
                                                        a.href = doc?.path;
                                                        a.target = "_blank";
                                                        document.body.appendChild(a);
                                                        a.click();
                                                        document.body.removeChild(a);
                                                    }}
                                                >
                                                    {doc?.filename}
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm font-rocGroteskMedium text-slate-500">
                                                        {doc?.fileSize &&
                                                            convertBytes(
                                                                Number(doc?.fileSize ?? 0)
                                                            )}
                                                    </p>
                                                    <p className="h-1 w-1 bg-slate-500 rounded-full"></p>
                                                    <p className="text-sm font-rocGroteskMedium text-[#0AAD0A]">
                                                        File uploaded
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {deleteId === doc?._id && deletingSpaceFile ? (
                                            <Loader size={4} />
                                        ) : (
                                            <i
                                                className="ri-delete-bin-line text-xl text-r-50 cursor-pointer"
                                                onClick={() => {
                                                    deleteAttachment(doc?._id);
                                                    setDeleteId(doc?._id);
                                                }}
                                            ></i>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {gettingSpaceFiles && isLoadingMore && (
                            <div className="flex justify-center my-4">
                                <Loader color="gm-25" size={4} />
                            </div>
                        )}
                    </>
                ) : search || memberId || fileTypes ? (
                    <div className="h-[200px] flex items-center justify-center max-w-[350px] mx-auto">
                        <p className="text-slate-500 font-rocGroteskMedium text-sm text-center">
                            Sorry, no results match your search. Refine your criteria and try again.
                        </p>
                    </div>
                ) : (
                    !gettingSpaceFiles &&
                    !uploadingDoc && (
                        <div className="flex justify-center items-center flex-col gap-4 mt-28 font-rocGroteskMedium">
                            <i className="ri-file-upload-line text-lg text-[#000000]"></i>
                            <p className="text-g-75 text-base">No files shared yet</p>

                            <p className="text-sm text-slate-500 max-w-[350px] text-center">
                                Files shared within this space will appear here. Upload or drop a
                                file to get started.
                            </p>
                            <div
                                className="flex items-center  w-fit gap-4 cursor-pointer"
                                onClick={() => document.getElementById("fileInput")?.click()}
                            >
                                <div className=" !py-2 !px-4 !w-auto  bg-y-10 rounded text-sm font-rocGroteskMedium !text-gm-50">
                                    Upload file
                                </div>
                                <input
                                    type="file"
                                    id="fileInput"
                                    className={`absolute z-[-1]  opacity-0`}
                                    onChange={(evt) => {
                                        if (evt?.target?.files?.length > 0) {
                                            if (
                                                !isFileSizeLessThanOrEqualTo(
                                                    evt.target.files[0],
                                                    50
                                                )
                                            ) {
                                                toast.custom((t) => (
                                                    <CustomToast
                                                        t={t}
                                                        message={
                                                            "The file size is too large. Please upload a file smaller than 50 MB."
                                                        }
                                                        type={"error"}
                                                    />
                                                ));
                                                return;
                                            }

                                            setFilesAttached({
                                                ...filesAttached,
                                                fileSize: evt.target.files[0].size.toString(),
                                                filename: evt.target.files[0].name,
                                                fileType: evt.target.files[0].type,
                                                url: "",
                                                path: "",
                                            });
                                            handleUpload(
                                                evt.target.files[0].name,
                                                evt.target.files[0],
                                                evt.target.files[0].size.toString()
                                            );
                                        }
                                    }}
                                    disabled={uploadingDoc}
                                />
                            </div>
                        </div>
                    )
                )}
            </div>
        </div>
    );
}
